import React, { useState, useMemo, useEffect, useCallback } from 'react';

const isRealImage = (url) => url && (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:'));

function SlotCard({ item, isEmpty, onTap }) {
  if (isEmpty) {
    return (
      <button
        onClick={onTap}
        className="w-full aspect-square rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center hover:border-[#D4A843] hover:bg-amber-50 transition-all active:scale-95"
      >
        <span className="text-2xl text-gray-300">+</span>
      </button>
    );
  }

  const img = item?.image_url;
  const isEmoji = !isRealImage(img);

  return (
    <button
      onClick={onTap}
      className="w-full aspect-square rounded-xl border-2 border-gray-200 bg-white flex flex-col items-center justify-center p-1.5 hover:border-[#D4A843] hover:shadow-md transition-all active:scale-95 relative"
    >
      {isEmoji ? (
        <span className="text-3xl">{img || '👕'}</span>
      ) : (
        <img src={img} alt="" className="w-10 h-10 rounded-lg object-cover" />
      )}
      <p className="text-[9px] font-semibold text-gray-700 mt-1 text-center leading-tight line-clamp-2">
        {item?.name_english || ''}
      </p>
      {item?.name_darija && (
        <p className="text-[7px] text-emerald-600 text-center leading-tight truncate w-full">{item.name_darija}</p>
      )}
    </button>
  );
}

export default function ClosetModal({
  ownedItems = [], allItems = [], closet = { outfits: [], accessories: [] },
  maxOutfits = 8, maxAccessories = 6,
  onUpdateCloset, onTrade, onSellItem, onDressUp, onClose, playSound, gameSettings = {},
  API, userId, coins = 0, initialView = 'closet'
}) {
  const s = gameSettings;
  const iconToken = s.icon_token || '🪙';

  const [view, setView] = useState(initialView); // closet | pick-item | item-actions | trade-pick | trade-confirm | trade-panel | p2p-list
  const [selectedSlotType, setSelectedSlotType] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [tradeItemId, setTradeItemId] = useState(null);
  const [tradeNewItem, setTradeNewItem] = useState(null);

  // Trade control panel state
  const [myListings, setMyListings] = useState([]);
  const [myMarketName, setMyMarketName] = useState('');
  const [tradeHistory, setTradeHistory] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelTab, setPanelTab] = useState('listings'); // 'listings' | 'offers' | 'history'
  const [respondingOfferId, setRespondingOfferId] = useState(null);
  const [p2pListItem, setP2pListItem] = useState(null);
  const [p2pWantedDesc, setP2pWantedDesc] = useState('');
  const [listAcceptItems, setListAcceptItems] = useState(true);
  const [listTokenPrice, setListTokenPrice] = useState('');
  const p2pEnabled = s.enable_p2p_trading === true;
  const [sellPending, setSellPending] = useState(false);
  const [sellResult, setSellResult] = useState(null);

  const p2pHeaders = useMemo(() => {
    const token = localStorage.getItem('user_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, []);

  const fetchMyListings = useCallback(async () => {
    if (!API || !p2pEnabled) return;
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings/mine`, { headers: p2pHeaders });
      if (res.ok) setMyListings(await res.json());
    } catch (err) { console.error('My listings fetch error:', err); }
  }, [API, p2pEnabled, p2pHeaders]);

  const fetchMarketplaceName = useCallback(async () => {
    if (!API || !userId) return;
    try {
      const res = await fetch(`${API}/souk-clothes/marketplace-name/${userId}`, { headers: p2pHeaders });
      if (res.ok) {
        const data = await res.json();
        setMyMarketName(data.marketplace_name || '');
      }
    } catch (err) { console.error('Marketplace name error:', err); }
  }, [API, userId, p2pHeaders]);

  const fetchTradeHistory = useCallback(async () => {
    if (!API || !userId) return;
    try {
      const res = await fetch(`${API}/souk-clothes/trade-history/${userId}`, { headers: p2pHeaders });
      if (res.ok) setTradeHistory(await res.json());
    } catch (err) { console.error('Trade history error:', err); }
  }, [API, userId, p2pHeaders]);

  const fetchReceivedOffers = useCallback(async () => {
    if (!API || !p2pEnabled) return;
    try {
      const res = await fetch(`${API}/souk-clothes/trade-offers/received`, { headers: p2pHeaders });
      if (res.ok) setReceivedOffers(await res.json());
    } catch (err) { console.error('Received offers error:', err); }
  }, [API, p2pEnabled, p2pHeaders]);

  const handleRespondOffer = async (offerId, action) => {
    if (!API) return;
    setRespondingOfferId(offerId);
    try {
      const res = await fetch(`${API}/souk-clothes/trade-offers/${offerId}/respond`, {
        method: 'POST', headers: p2pHeaders,
        body: JSON.stringify({ action })
      });
      if (res.ok) {
        playSound?.(action === 'accept' ? 'success' : 'tick');
        fetchReceivedOffers();
        fetchMyListings();
        fetchTradeHistory();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to respond');
      }
    } catch (err) { console.error('Respond error:', err); }
    setRespondingOfferId(null);
  };

  const handleRegenerateName = async () => {
    if (!API || !userId) return;
    try {
      const res = await fetch(`${API}/souk-clothes/marketplace-name/${userId}/regenerate`, {
        method: 'POST', headers: p2pHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setMyMarketName(data.marketplace_name || '');
        playSound?.('tick');
      }
    } catch (err) { console.error('Regenerate name error:', err); }
  };

  // Fetch trade panel data when opening
  useEffect(() => {
    if (p2pEnabled && view === 'trade-panel') {
      setPanelLoading(true);
      Promise.all([fetchMyListings(), fetchMarketplaceName(), fetchTradeHistory(), fetchReceivedOffers()])
        .finally(() => setPanelLoading(false));
    }
  }, [p2pEnabled, view, fetchMyListings, fetchMarketplaceName, fetchTradeHistory, fetchReceivedOffers]);

  // Map item IDs to item objects
  const itemMap = useMemo(() => {
    const map = {};
    allItems.forEach(i => { map[i.id] = i; });
    return map;
  }, [allItems]);

  const outfitItems = closet.outfits.map(id => itemMap[id]).filter(Boolean);
  const accessoryItems = closet.accessories.map(id => itemMap[id]).filter(Boolean);

  const availableForCloset = useMemo(() => {
    const inCloset = new Set([...closet.outfits, ...closet.accessories]);
    return ownedItems
      .filter(id => !inCloset.has(id))
      .map(id => itemMap[id])
      .filter(Boolean);
  }, [ownedItems, closet, itemMap]);

  const pickableItems = useMemo(() => {
    const isAcc = (i) => { const lt = (i.layer_type || i.category || '').toLowerCase(); return lt === 'accessories' || lt === 'accessory'; };
    if (selectedSlotType === 'accessories') {
      return availableForCloset.filter(isAcc);
    }
    return availableForCloset.filter(i => !isAcc(i));
  }, [availableForCloset, selectedSlotType]);

  const tradeableItems = useMemo(() => {
    const ownedSet = new Set(ownedItems);
    return allItems.filter(i => !ownedSet.has(i.id) && i.is_active !== false && !i.is_starter && !i.is_free);
  }, [allItems, ownedItems]);

  const selectedItem = selectedItemId ? itemMap[selectedItemId] : null;
  const tradeGiveItem = tradeItemId ? itemMap[tradeItemId] : null;

  const handleEmptySlotTap = (slotType) => {
    setSelectedSlotType(slotType);
    setView('pick-item');
    playSound?.('tick');
  };

  const handleFilledSlotTap = (itemId) => {
    setSelectedItemId(itemId);
    setView('item-actions');
    playSound?.('tick');
  };

  const handleAddToCloset = (item) => {
    const newCloset = { ...closet };
    if (selectedSlotType === 'accessories') {
      if (newCloset.accessories.length >= maxAccessories) return;
      newCloset.accessories = [...newCloset.accessories, item.id];
    } else {
      if (newCloset.outfits.length >= maxOutfits) return;
      newCloset.outfits = [...newCloset.outfits, item.id];
    }
    onUpdateCloset?.(newCloset);
    setView('closet');
    playSound?.('tick');
  };

  const handleRemoveFromCloset = () => {
    if (!selectedItemId) return;
    const newCloset = {
      outfits: closet.outfits.filter(id => id !== selectedItemId),
      accessories: closet.accessories.filter(id => id !== selectedItemId),
    };
    onUpdateCloset?.(newCloset);
    setSelectedItemId(null);
    setView('closet');
    playSound?.('tick');
  };

  const handleStartTrade = () => {
    setTradeItemId(selectedItemId);
    setTradeNewItem(null);
    setView('trade-pick');
    playSound?.('tick');
  };

  const handlePickTradeItem = (item) => {
    setTradeNewItem(item);
    setView('trade-confirm');
    playSound?.('tick');
  };

  const handleConfirmTrade = () => {
    if (!tradeItemId || !tradeNewItem) return;
    onTrade?.(tradeItemId, tradeNewItem);
    setTradeItemId(null);
    setTradeNewItem(null);
    setSelectedItemId(null);
    setView('closet');
    playSound?.('coin');
  };

  const goBackToCloset = () => {
    setSelectedItemId(null);
    setTradeItemId(null);
    setTradeNewItem(null);
    setP2pListItem(null);
    setP2pWantedDesc('');
    setListAcceptItems(true);
    setListTokenPrice('');
    setView('closet');
    playSound?.('tick');
  };

  const handleCreateP2pListing = async () => {
    if (!p2pListItem || !API) return;
    const tokenPrice = parseInt(listTokenPrice) || 0;
    if (!listAcceptItems && tokenPrice <= 0) {
      alert(s.mp_validation_trade_or_price || 'Must accept item trades or set a token price');
      return;
    }
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings`, {
        method: 'POST', headers: p2pHeaders,
        body: JSON.stringify({
          item_id: p2pListItem.id,
          wanted_description: p2pWantedDesc,
          accept_items: listAcceptItems,
          token_price: tokenPrice
        })
      });
      if (res.ok) {
        playSound?.('success');
        // Remove listed item from closet
        const listedId = p2pListItem.id;
        const newCloset = {
          outfits: closet.outfits.filter(id => id !== listedId),
          accessories: closet.accessories.filter(id => id !== listedId),
        };
        onUpdateCloset?.(newCloset);
        setP2pListItem(null);
        setP2pWantedDesc('');
        setListAcceptItems(true);
        setListTokenPrice('');
        setView('trade-panel');
        fetchMyListings();
      } else {
        const err = await res.json();
        alert(err.detail || s.mp_error_create_listing || 'Failed to create listing');
      }
    } catch (err) { console.error('P2P list error:', err); }
  };

  const handleCancelP2pListing = async (listingId, itemId) => {
    if (!API) return;
    try {
      const res = await fetch(`${API}/souk-clothes/trade-listings/${listingId}`, {
        method: 'DELETE', headers: p2pHeaders
      });
      if (res.ok) {
        playSound?.('tick');
        // Re-add item to closet if space
        if (itemId) {
          const it = itemMap[itemId];
          if (it) {
            const lt = (it.layer_type || it.category || '').toLowerCase();
            const isAcc = lt === 'accessories' || lt === 'accessory';
            const newCloset = { outfits: [...closet.outfits], accessories: [...closet.accessories] };
            if (isAcc && newCloset.accessories.length < maxAccessories && !newCloset.accessories.includes(itemId)) {
              newCloset.accessories.push(itemId);
              onUpdateCloset?.(newCloset);
            } else if (!isAcc && newCloset.outfits.length < maxOutfits && !newCloset.outfits.includes(itemId)) {
              newCloset.outfits.push(itemId);
              onUpdateCloset?.(newCloset);
            }
          }
        }
        fetchMyListings();
      }
    } catch (err) { console.error('P2P cancel error:', err); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-up">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {view === 'closet' ? `👗 ${s.closet_title || 'My Closet'}` :
             view === 'pick-item' ? (s.closet_pick_title || 'Pick an Item') :
             view === 'item-actions' ? (s.closet_options_title || 'Item Options') :
             view === 'trade-pick' ? (s.closet_pick_new || 'Pick New Item') :
             view === 'trade-confirm' ? (s.closet_confirm_trade || 'Confirm Trade') :
             view === 'trade-panel' ? (s.tp_title || 'Trade Panel') :
             view === 'p2p-list' ? (s.mp_set_trade_options || 'List for Trade') :
             ''}
          </h2>
          <button
            onClick={view === 'closet' ? onClose : (view === 'p2p-list') ? () => setView('trade-panel') : goBackToCloset}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors text-gray-600"
          >
            {view === 'closet' ? '✕' : '←'}
          </button>
        </div>

        <div className="p-5">

          {/* VIEW: CLOSET — main slot grid */}
          {view === 'closet' && (
            <>
              {/* Outfits section */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-700">{s.closet_outfits_label || 'Outfits'}</p>
                  <p className="text-xs text-gray-400">{outfitItems.length}/{maxOutfits}</p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {outfitItems.map(item => (
                    <SlotCard key={item.id} item={item} isEmpty={false} onTap={() => handleFilledSlotTap(item.id)} />
                  ))}
                  {Array.from({ length: maxOutfits - outfitItems.length }, (_, i) => (
                    <SlotCard key={`empty-o-${i}`} isEmpty onTap={() => handleEmptySlotTap('outfits')} />
                  ))}
                </div>
              </div>

              {/* Accessories section */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-gray-700">{s.closet_accessories_label || 'Accessories'}</p>
                  <p className="text-xs text-gray-400">{accessoryItems.length}/{maxAccessories}</p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {accessoryItems.map(item => (
                    <SlotCard key={item.id} item={item} isEmpty={false} onTap={() => handleFilledSlotTap(item.id)} />
                  ))}
                  {Array.from({ length: maxAccessories - accessoryItems.length }, (_, i) => (
                    <SlotCard key={`empty-a-${i}`} isEmpty onTap={() => handleEmptySlotTap('accessories')} />
                  ))}
                </div>
              </div>

              {/* Hint note */}
              <p className="text-xs text-center text-gray-400 mb-3">
                {s.closet_hint || 'Tap an item to sell or trade it'}
              </p>

              {/* Bottom actions */}
              <div className="flex gap-2.5 flex-wrap">
                <button
                  onClick={onDressUp}
                  className="flex-1 py-2.5 rounded-xl bg-[#D4A843] hover:bg-[#c09a3b] text-white font-bold text-sm transition-colors"
                >
                  {s.icon_style_menu || '👤'} {s.closet_dressup_button || 'Dress Up Avatar'}
                </button>
                {p2pEnabled && (
                  <button
                    onClick={() => { setView('trade-panel'); playSound?.('tick'); }}
                    className="flex-1 py-2.5 rounded-xl bg-[#C84B31] hover:bg-[#a33d27] text-white font-bold text-sm transition-colors"
                  >
                    {s.icon_trade || '🔄'} {s.tp_button || 'Trade Panel'}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
                >
                  {s.closet_close_button || 'Close'}
                </button>
              </div>
            </>
          )}

          {/* VIEW: PICK ITEM — choose item to add to closet */}
          {view === 'pick-item' && (
            <>
              {pickableItems.length === 0 ? (
                <div className="text-center py-8">
                  <span className="text-4xl block mb-3">📦</span>
                  <p className="text-gray-500 text-sm">{s.closet_empty_title || 'No items available to add'}</p>
                  <p className="text-gray-400 text-xs mt-1">{s.closet_empty_desc || 'Buy items from merchants first!'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2.5">
                  {pickableItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleAddToCloset(item)}
                      className="bg-gray-50 rounded-xl border-2 border-gray-100 hover:border-[#D4A843] p-3 text-center transition-all hover:shadow-md active:scale-95"
                    >
                      {isRealImage(item.image_url) ? (
                        <img src={item.image_url} alt="" className="w-10 h-10 rounded-lg object-cover mx-auto mb-1" />
                      ) : (
                        <span className="text-3xl block mb-1">{isRealImage(item.image_url) ? '' : (item.image_url || '👕')}</span>
                      )}
                      <p className="text-[10px] font-bold text-gray-700 leading-tight">{item.name_english}</p>
                      {item.name_darija && (
                        <p className="text-[9px] text-[#2D6A4F]">{item.name_darija}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* VIEW: ITEM ACTIONS — remove, trade, or sell */}
          {view === 'item-actions' && selectedItem && (
            <div className="text-center">
              <div className="bg-gray-50 rounded-xl p-5 mb-4">
                {isRealImage(selectedItem.image_url) ? (
                  <img src={selectedItem.image_url} alt="" className="w-20 h-20 rounded-xl object-cover mx-auto mb-2" />
                ) : (
                  <span className="text-5xl block mb-2">{selectedItem.image_url && !isRealImage(selectedItem.image_url) ? selectedItem.image_url : '👕'}</span>
                )}
                <p className="font-bold text-gray-900">{selectedItem.name_english}</p>
                {selectedItem.name_darija && (
                  <p className="text-sm text-[#2D6A4F] font-medium">{selectedItem.name_darija}</p>
                )}
                <p className="text-xs text-amber-600 font-semibold mt-1">{s.closet_value_label || 'Value'}: {iconToken} {selectedItem.base_price_tokens}</p>
              </div>

              {sellResult ? (
                <div className="space-y-2.5">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <span className="text-3xl block mb-2">💰</span>
                    <p className="font-bold text-green-800">{s.closet_sold_title || 'Sold!'}</p>
                    <p className="text-sm text-green-700 mt-1">
                      {s.closet_sold_received || 'You received'} {iconToken} {sellResult.sell_value}
                    </p>
                  </div>
                  <button
                    onClick={() => { setSellResult(null); goBackToCloset(); }}
                    className="w-full py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                  >
                    {s.closet_back_button || 'Back to Closet'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {!selectedItem.is_starter && !selectedItem.is_free && (
                    <button
                      onClick={handleStartTrade}
                      className="w-full py-3 rounded-xl bg-[#C84B31] hover:bg-[#a33d27] text-white font-bold text-sm transition-colors"
                    >
                      {s.icon_trade || '🔄'} {s.closet_trade_button || 'Trade for Something New'}
                    </button>
                  )}
                  {!selectedItem.is_starter && !selectedItem.is_free && (selectedItem.base_price_tokens || 0) > 0 && onSellItem && (
                    <button
                      onClick={async () => {
                        setSellPending(true);
                        const res = await onSellItem(selectedItemId);
                        setSellPending(false);
                        if (res?.success) {
                          setSellResult(res);
                          playSound?.('coin');
                        }
                      }}
                      disabled={sellPending}
                      className={`w-full py-3 rounded-xl font-bold text-sm transition-colors ${
                        sellPending
                          ? 'bg-gray-200 text-gray-400'
                          : 'bg-amber-500 hover:bg-amber-600 text-white'
                      }`}
                    >
                      {sellPending ? (s.closet_selling || 'Selling...') : `💰 ${s.closet_sell_for || 'Sell for'} ${iconToken} ${Math.floor((selectedItem.base_price_tokens || 0) * 0.4)}`}
                    </button>
                  )}
                  <button
                    onClick={handleRemoveFromCloset}
                    className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:border-red-200 hover:text-red-600 transition-colors"
                  >
                    {s.closet_remove_button || 'Remove from Closet'}
                  </button>
                  <button
                    onClick={goBackToCloset}
                    className="w-full py-2.5 text-gray-400 text-sm hover:text-gray-600 transition-colors"
                  >
                    {s.closet_cancel || 'Cancel'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* VIEW: TRADE PICK — choose new item to receive */}
          {view === 'trade-pick' && tradeGiveItem && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white border border-amber-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {isRealImage(tradeGiveItem.image_url) ? (
                      <img src={tradeGiveItem.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl">{tradeGiveItem.image_url || '👕'}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-amber-800 font-bold truncate">{tradeGiveItem.name_english}</p>
                    <p className="text-[10px] text-amber-600">
                      {s.closet_original_value || 'Value'}: {iconToken} {tradeGiveItem.base_price_tokens || 0}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-green-700">{iconToken} {Math.floor((tradeGiveItem.base_price_tokens || 0) * 0.5)}</p>
                    <p className="text-[9px] text-amber-600">{s.closet_trade_credit_short || '50% credit'}</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 font-semibold mb-3">{s.closet_pick_new_hint || 'Pick a new item:'}</p>
              {tradeableItems.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-400 text-sm">{s.closet_no_trade_items || 'No items available for trade'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2.5 max-h-[40vh] overflow-y-auto">
                  {tradeableItems.map(item => {
                    const tradeCredit = Math.floor((tradeGiveItem.base_price_tokens || 0) * 0.5);
                    const diff = (item.base_price_tokens || 0) - tradeCredit;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handlePickTradeItem(item)}
                        className="bg-gray-50 rounded-xl border-2 border-gray-100 hover:border-[#D4A843] p-2.5 text-center transition-all hover:shadow-md active:scale-95"
                      >
                        {isRealImage(item.image_url) ? (
                          <img src={item.image_url} alt="" className="w-9 h-9 rounded-lg object-cover mx-auto mb-1" />
                        ) : (
                          <span className="text-2xl block mb-1">{isRealImage(item.image_url) ? '' : (item.image_url || '👕')}</span>
                        )}
                        <p className="text-[9px] font-bold text-gray-700 leading-tight line-clamp-2">{item.name_english}</p>
                        <p className={`text-[9px] font-semibold mt-0.5 ${diff > 0 ? 'text-red-500' : 'text-green-600'}`}>
                          {diff > 0 ? `+${iconToken}${diff}` : diff < 0 ? `${iconToken}${Math.abs(diff)} ${s.closet_back_word || 'back'}` : (s.closet_even_swap || 'Even swap')}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* VIEW: TRADE CONFIRM */}
          {view === 'trade-confirm' && tradeGiveItem && tradeNewItem && (
            <div>
              <div className="flex items-center gap-3 justify-center mb-5">
                <div className="bg-red-50 rounded-xl p-3 text-center flex-1 border border-red-100">
                  <p className="text-[10px] text-red-500 font-bold mb-1">{s.closet_give_label || 'GIVE'}</p>
                  {isRealImage(tradeGiveItem.image_url) ? (
                    <img src={tradeGiveItem.image_url} alt="" className="w-12 h-12 rounded-lg object-cover mx-auto mb-1" />
                  ) : (
                    <span className="text-3xl block mb-1">{tradeGiveItem.image_url && !isRealImage(tradeGiveItem.image_url) ? tradeGiveItem.image_url : '👕'}</span>
                  )}
                  <p className="text-[10px] font-bold text-gray-700">{tradeGiveItem.name_english}</p>
                </div>

                <span className="text-2xl text-gray-400">→</span>

                <div className="bg-green-50 rounded-xl p-3 text-center flex-1 border border-green-100">
                  <p className="text-[10px] text-green-600 font-bold mb-1">{s.closet_get_label || 'GET'}</p>
                  {isRealImage(tradeNewItem.image_url) ? (
                    <img src={tradeNewItem.image_url} alt="" className="w-12 h-12 rounded-lg object-cover mx-auto mb-1" />
                  ) : (
                    <span className="text-3xl block mb-1">{tradeNewItem.image_url && !isRealImage(tradeNewItem.image_url) ? tradeNewItem.image_url : '👕'}</span>
                  )}
                  <p className="text-[10px] font-bold text-gray-700">{tradeNewItem.name_english}</p>
                </div>
              </div>

              {(() => {
                const tradeCredit = Math.floor((tradeGiveItem.base_price_tokens || 0) * 0.5);
                const newPrice = tradeNewItem.base_price_tokens || 0;
                const diff = newPrice - tradeCredit;
                return (
                  <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{s.closet_trade_credit || 'Trade credit (50%)'}</span>
                      <span className="text-green-600 font-semibold">{iconToken} {tradeCredit}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{s.closet_new_item_price || 'New item price'}</span>
                      <span className="text-gray-700 font-semibold">{iconToken} {newPrice}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-1.5 flex justify-between text-sm">
                      <span className="font-bold text-gray-700">
                        {diff > 0 ? (s.closet_you_pay || 'You pay') : diff < 0 ? (s.closet_you_get_back || 'You get back') : (s.closet_even_swap || 'Even swap')}
                      </span>
                      <span className={`font-bold ${diff > 0 ? 'text-red-600' : diff < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {diff !== 0 ? `${iconToken} ${Math.abs(diff)}` : `${iconToken} 0`}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-2.5">
                <button
                  onClick={handleConfirmTrade}
                  className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
                >
                  {s.closet_confirm_button || 'Confirm Trade'}
                </button>
                <button
                  onClick={() => { setTradeNewItem(null); setView('trade-pick'); }}
                  className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
                >
                  {s.closet_pick_different || '← Pick Different Item'}
                </button>
              </div>
            </div>
          )}

          {/* VIEW: TRADE PANEL — personal trade dashboard */}
          {view === 'trade-panel' && p2pEnabled && (
            <div>
              {panelLoading && <p className="text-center text-gray-400 text-sm py-4">{s.tp_loading || 'Loading...'}</p>}

              {/* Your marketplace identity */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-[10px] text-amber-600 font-bold mb-1">{s.tp_your_name_label || 'Your Marketplace Name'}</p>
                <div className="flex items-center justify-between">
                  <p className="text-base font-bold text-gray-900">{myMarketName || '...'}</p>
                  <button
                    onClick={handleRegenerateName}
                    className="text-[10px] text-amber-700 hover:text-amber-900 font-semibold px-2 py-1 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    {s.tp_new_name || 'New Name'}
                  </button>
                </div>
                <p className="text-[10px] text-amber-500 mt-1">{s.tp_name_desc || 'This is how other traders see you'}</p>
              </div>

              {/* Tabs: My Listings | Offers | History */}
              <div className="flex gap-1 mb-4 bg-gray-100 rounded-xl p-1">
                {[
                  { id: 'listings', label: s.tp_tab_listings || 'My Listings', count: myListings.length },
                  { id: 'offers', label: s.tp_tab_offers || 'Offers', count: receivedOffers.length },
                  { id: 'history', label: s.tp_tab_history || 'History', count: tradeHistory.length },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setPanelTab(tab.id); playSound?.('tick'); }}
                    className={`flex-1 py-2 px-2 rounded-lg text-xs font-semibold transition-all ${
                      panelTab === tab.id
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded-full ${
                        panelTab === tab.id ? 'bg-[#2D6A4F] text-white' : 'bg-gray-300 text-gray-600'
                      }`}>{tab.count}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* TAB: My Listings */}
              {panelTab === 'listings' && (
                <div className="mb-4">
                  <div className="flex items-center justify-end mb-2">
                    <button
                      onClick={() => { setP2pListItem(null); setView('p2p-list'); playSound?.('tick'); }}
                      className="text-xs text-[#2D6A4F] font-bold hover:underline"
                    >
                      {s.tp_add_listing || '+ Add Listing'}
                    </button>
                  </div>
                  {myListings.length === 0 ? (
                    <div className="bg-gray-50 rounded-xl p-6 text-center">
                      <span className="text-3xl block mb-2">📋</span>
                      <p className="text-gray-400 text-xs">{s.tp_no_listings || 'No active listings'}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {myListings.map(l => (
                        <div key={l.id} className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white border border-blue-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {isRealImage(l.item_image) ? (
                                <img src={l.item_image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-lg">{l.item_image || '👕'}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-gray-800 truncate">{l.item_name}</p>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {l.accept_items !== false && (
                                  <span className="text-[8px] bg-blue-100 text-blue-700 font-semibold px-1 py-0.5 rounded">{s.mp_item_trade_tag || 'Item Trade'}</span>
                                )}
                                {(l.token_price || 0) > 0 && (
                                  <span className="text-[8px] bg-amber-100 text-amber-700 font-semibold px-1 py-0.5 rounded">{iconToken} {l.token_price}</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleCancelP2pListing(l.id, l.item_id)}
                              className="text-[10px] text-red-500 hover:text-red-700 font-bold px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                            >
                              {s.tp_cancel_listing || 'Cancel'}
                            </button>
                          </div>
                          <div className="mt-2 pt-2 border-t border-blue-100 flex items-center gap-3">
                            <span className="text-[10px] text-gray-500">
                              👀 {l.view_count || 0} {(l.view_count || 0) !== 1 ? (s.tp_views_plural || 'views') : (s.tp_view_singular || 'view')}
                            </span>
                            <span className="text-[10px] text-gray-500">
                              🤝 {l.offer_count || 0} {(l.offer_count || 0) !== 1 ? (s.mp_offers_plural || 'offers') : (s.mp_offer_singular || 'offer')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB: Offers — incoming trade/buy offers */}
              {panelTab === 'offers' && (
                <div className="mb-4">
                  {receivedOffers.length === 0 ? (
                    <div className="bg-gray-50 rounded-xl p-6 text-center">
                      <span className="text-3xl block mb-2">📬</span>
                      <p className="text-gray-400 text-xs">{s.tp_no_offers || 'No pending offers'}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {receivedOffers.map(o => (
                        <div key={o.id} className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                          <div className="flex items-start gap-3">
                            {/* Their offered item */}
                            <div className="flex-shrink-0 text-center">
                              <div className="w-10 h-10 rounded-lg bg-white border border-orange-200 flex items-center justify-center overflow-hidden">
                                {isRealImage(o.offer_item_image) ? (
                                  <img src={o.offer_item_image} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-lg">{o.offer_item_image || '👕'}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-700">
                                <span className="font-bold text-gray-900">{o.offerer_name}</span> {s.tp_wants_to_trade || 'wants to trade'}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {s.tp_their_item || 'Their'}: <span className="font-bold">{o.offer_item_name}</span> → {s.tp_your_item || 'Your'}: <span className="font-bold">{o.listing_item_name}</span>
                              </p>
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2 pt-2 border-t border-orange-100">
                            <button
                              onClick={() => handleRespondOffer(o.id, 'accept')}
                              disabled={respondingOfferId === o.id}
                              className="flex-1 py-2 rounded-lg bg-[#2D6A4F] hover:bg-[#40916C] text-white text-xs font-bold transition-colors disabled:bg-gray-300"
                            >
                              {respondingOfferId === o.id ? '...' : (s.tp_accept || 'Accept')}
                            </button>
                            <button
                              onClick={() => handleRespondOffer(o.id, 'decline')}
                              disabled={respondingOfferId === o.id}
                              className="flex-1 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold transition-colors disabled:bg-gray-100"
                            >
                              {s.tp_decline || 'Decline'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB: History */}
              {panelTab === 'history' && (
                <div className="mb-4">
                  {tradeHistory.length === 0 ? (
                    <div className="bg-gray-50 rounded-xl p-6 text-center">
                      <span className="text-3xl block mb-2">📜</span>
                      <p className="text-gray-400 text-xs">{s.tp_no_history || 'No trades yet'}</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                      {tradeHistory.map(tx => (
                        <div key={tx.id} className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{tx.type === 'p2p_trade' ? '🤝' : '💰'}</span>
                            <div className="flex-1 min-w-0">
                              {tx.type === 'p2p_trade' ? (
                                <p className="text-xs text-gray-700">
                                  {s.tp_traded || 'Traded'} <span className="font-bold">{tx.gave_item?.name || '?'}</span> → <span className="font-bold">{tx.received_item?.name || '?'}</span>
                                </p>
                              ) : tx.tokens_spent ? (
                                <p className="text-xs text-gray-700">
                                  {s.tp_bought || 'Bought'} <span className="font-bold">{tx.received_item?.name || '?'}</span> {s.tp_for || 'for'} {iconToken} {tx.tokens_spent}
                                </p>
                              ) : (
                                <p className="text-xs text-gray-700">
                                  {s.tp_sold || 'Sold'} <span className="font-bold">{tx.gave_item?.name || '?'}</span> {s.tp_for || 'for'} {iconToken} {tx.tokens_earned}
                                </p>
                              )}
                              <p className="text-[10px] text-gray-400">
                                {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={goBackToCloset}
                className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
              >
                {s.tp_back_to_closet || '← Back to Closet'}
              </button>
            </div>
          )}

          {/* VIEW: P2P LIST — pick own item to list for trade */}
          {view === 'p2p-list' && p2pEnabled && (
            <div>
              {!p2pListItem ? (
                <>
                  <p className="text-xs text-gray-500 mb-3">{s.mp_pick_item_title || 'Pick an item to list for trade:'}</p>
                  <div className="grid grid-cols-3 gap-2.5 max-h-[40vh] overflow-y-auto">
                    {ownedItems.filter(id => {
                      const item = itemMap[id];
                      return item && !item.is_starter && !item.is_free;
                    }).map(id => {
                      const item = itemMap[id];
                      const alreadyListed = myListings.some(l => l.item_id === id);
                      return (
                        <button
                          key={id}
                          disabled={alreadyListed}
                          onClick={() => { setP2pListItem(item); playSound?.('tick'); }}
                          className={`rounded-xl border-2 p-2.5 text-center transition-all active:scale-95 ${
                            alreadyListed ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed' : 'border-gray-100 bg-gray-50 hover:border-[#D4A843] hover:shadow-md'
                          }`}
                        >
                          {isRealImage(item?.image_url) ? (
                            <img src={item.image_url} alt="" className="w-9 h-9 rounded-lg object-cover mx-auto mb-1" />
                          ) : (
                            <span className="text-2xl block mb-1">{item?.image_url || '👕'}</span>
                          )}
                          <p className="text-[9px] font-bold text-gray-700 leading-tight line-clamp-2">{item?.name_english}</p>
                          <p className="text-[9px] text-gray-400">{iconToken} {item?.base_price_tokens || 0}</p>
                          {alreadyListed && <p className="text-[8px] text-amber-500 font-bold">{s.mp_already_listed || 'Listed'}</p>}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-center">
                    {isRealImage(p2pListItem.image_url) ? (
                      <img src={p2pListItem.image_url} alt="" className="w-14 h-14 rounded-lg object-cover mx-auto mb-2" />
                    ) : (
                      <span className="text-4xl block mb-2">{p2pListItem.image_url || '👕'}</span>
                    )}
                    <p className="text-sm font-bold text-gray-800">{p2pListItem.name_english}</p>
                    <p className="text-xs text-amber-600">{iconToken} {p2pListItem.base_price_tokens || 0}</p>
                  </div>

                  {/* Trade type options */}
                  <div className="mb-4">
                    <label className="text-xs font-bold text-gray-600 block mb-2">{s.mp_how_to_trade || 'How do you want to trade?'}</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-200 hover:border-[#D4A843] transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={listAcceptItems}
                          onChange={e => setListAcceptItems(e.target.checked)}
                          className="w-4 h-4 rounded text-[#2D6A4F] focus:ring-[#2D6A4F]"
                        />
                        <div>
                          <p className="text-xs font-bold text-gray-700">{s.mp_accept_items_label || '🤝 Accept item trades'}</p>
                          <p className="text-[10px] text-gray-400">{s.mp_accept_items_desc || 'Other players can offer their items'}</p>
                        </div>
                      </label>
                      <div className="p-3 rounded-xl border-2 border-gray-200">
                        <div className="flex items-center gap-3">
                          <span className="text-sm">{iconToken}</span>
                          <div className="flex-1">
                            <p className="text-xs font-bold text-gray-700">{s.mp_token_price_label || 'Set token price (optional)'}</p>
                            <input
                              type="number"
                              value={listTokenPrice}
                              onChange={e => setListTokenPrice(e.target.value)}
                              placeholder="0"
                              min="0"
                              className="w-full mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:border-[#D4A843] focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs font-bold text-gray-600 block mb-1.5">{s.mp_want_label || 'What do you want in return? (optional)'}</label>
                    <input
                      type="text"
                      value={p2pWantedDesc}
                      onChange={e => setP2pWantedDesc(e.target.value)}
                      placeholder={s.mp_want_placeholder || 'e.g. jacket, shoes, accessories...'}
                      className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 text-sm focus:border-[#D4A843] focus:outline-none transition-colors"
                      maxLength={100}
                    />
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 mb-4 text-center">
                    <p className="text-[10px] text-gray-500">{s.mp_anonymous_note || 'Your listing will be anonymous — other traders see your marketplace name only'}</p>
                  </div>
                  <div className="space-y-2.5">
                    <button
                      onClick={handleCreateP2pListing}
                      disabled={!listAcceptItems && (parseInt(listTokenPrice) || 0) <= 0}
                      className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                    >
                      {s.mp_confirm_list || 'List for Trade'}
                    </button>
                    <button
                      onClick={() => setP2pListItem(null)}
                      className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
                    >
                      {s.mp_pick_different || '← Pick Different'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
